// StatusPage.tsx — what the daemon is doing right now, and what it has just
// done. A row of figures across the top, the run log underneath.
//
// The page polls rather than offering a refresh button. A queue that is
// draining is the whole subject: a number that was true when you opened the
// tab is worse than no number, because it looks live. Five seconds is short
// enough that the queue depth visibly moves and long enough that a dashboard
// left open all day costs the API a request every five seconds and nothing
// else — /api/status is a read of counters the daemon already keeps.
import { useEffect, useState } from 'react';
import Alert from 'react-bootstrap/Alert';
import Card from 'react-bootstrap/Card';
import Spinner from 'react-bootstrap/Spinner';
import {
  BsCloudArrowUp, BsCpu, BsExclamationOctagon, BsGit, BsSpeedometer2, BsStack,
} from 'react-icons/bs';
import { StatusBadge } from 'common';
import { fetchRuns, fetchStatus } from '../api';
import type { ModelThroughput, Run, Status } from '../api';
import { absoluteTime, formatCount, formatTps, relativeTime } from '../format';
import Metric from '../components/Metric';
import RunTable from '../components/RunTable';

const POLL_MS = 5000;
const RUN_LIMIT = 25;

export default function StatusPage() {
  const [status, setStatus] = useState<Status | null>(null);
  const [runs, setRuns] = useState<Run[]>([]);
  const [error, setError] = useState('');

  useEffect(() => {
    let live = true;
    const load = async () => {
      try {
        const [next, recent] = await Promise.all([fetchStatus(), fetchRuns({ limit: RUN_LIMIT })]);
        if (!live) return;
        setStatus(next);
        setRuns(recent);
        setError('');
      } catch (err) {
        // A failed poll clears nothing: the last good numbers stay on screen
        // under the banner, which is what an operator wants when the daemon
        // is the thing that just went away.
        if (live) setError(err instanceof Error ? err.message : 'Failed to load status');
      }
    };
    void load();
    const timer = window.setInterval(() => void load(), POLL_MS);
    return () => { live = false; window.clearInterval(timer); };
  }, []);

  if (!status) {
    return error
      ? <Alert variant="danger">{error}</Alert>
      : <div className="d-flex justify-content-center py-5"><Spinner animation="border" /></div>;
  }

  const idleWorkers = Math.max(0, status.workers - status.in_flight);
  const pausedRepos = Math.max(0, status.repos_total - status.repos_enabled);

  return (
    <div>
      <div className="d-flex align-items-center justify-content-between flex-wrap gap-2 mb-3">
        <h1 className="m-0">Status</h1>
        {status.paused && <StatusBadge tone="warning">paused</StatusBadge>}
      </div>

      {error && <Alert variant="danger">{error}</Alert>}
      {status.paused && (
        <Alert variant="warning">
          <Alert.Heading className="h6">Indexing is paused</Alert.Heading>
          Pushes are still recorded, and nothing is being documented until an admin resumes.
        </Alert>
      )}

      <div className="card-grid mb-4">
        <Metric
          icon={<BsStack className="text-primary" />}
          label="Queue"
          value={String(status.queue_depth)}
          badge={status.overflow_active ? <StatusBadge tone="warning">overflow</StatusBadge> : undefined}
          note={status.overflow_active
            ? <>On <code>{status.overflow_model}</code> above {status.overflow_threshold} queued;
              back to <code>{status.base_model}</code> below {status.overflow_clear}.</>
            : <>On <code>{status.active_model}</code>; switches to OpenRouter past {status.overflow_threshold} queued.</>}
        />
        <Metric
          icon={<BsCpu className="text-info" />}
          label="Workers"
          value={`${status.in_flight}/${status.workers}`}
          note={idleWorkers > 0
            ? `${idleWorkers} idle` : 'All busy'}
        />
        <Metric
          icon={<BsGit className="text-success" />}
          label="Repos"
          value={`${status.repos_enabled}/${status.repos_total}`}
          note={pausedRepos > 0
            ? `${pausedRepos} paused — pushes there are ignored.`
            : 'Every known repository is being watched.'}
        />
        <Metric
          icon={<BsExclamationOctagon className={status.jobs_failed_total > 0 ? 'text-danger' : 'text-muted'} />}
          label="Jobs"
          value={formatCount(status.jobs_completed_total)}
          note={status.jobs_failed_total > 0
            ? `${formatCount(status.jobs_failed_total)} failed since start.`
            : 'None failed since start.'}
        />
        <Metric
          icon={<BsSpeedometer2 className="text-warning" />}
          label="Performance"
          value={`${formatTps(status.tokens_per_sec)} tok/s`}
          note={<ThroughputNote byModel={status.tokens_per_sec_by_model} />}
        />
      </div>

      <Card>
        <Card.Header className="d-flex align-items-center justify-content-between gap-2">
          <span>Runs</span>
          {status.runs_active > 0 && (
            <StatusBadge tone="primary" uppercase={false}>
              {status.runs_active} active
            </StatusBadge>
          )}
        </Card.Header>
        <Card.Body>
          {/* Every repository's runs together, so the log reads as the daemon's
              day rather than as one project's. Expanding a row shows the jobs
              in the level order the scheduler runs them. */}
          <RunTable runs={runs} showRepo />
        </Card.Body>
        <Card.Footer className="small text-muted">
          <span title={absoluteTime(status.last_run_at)}>Last run {relativeTime(status.last_run_at)}</span>
          {' · '}
          {status.auto_commit
            ? <><BsCloudArrowUp className="me-1" />Finished runs commit <code>.docs/</code> back to the repo</>
            : 'Auto-commit is off — documents are written to disk and not pushed'}
        </Card.Footer>
      </Card>
    </div>
  );
}

// ThroughputNote is the performance tile's caption: which models produced the
// last minute of tokens, and how fast each one went.
//
// Per model rather than one total because the total hides the thing worth
// seeing. A minute that spans a failover contains work from two models at very
// different speeds, and a single figure averages them into a number that
// describes neither — while "local 4.2 · hosted 61.0" says plainly that the
// fast one is carrying the queue.
function ThroughputNote({ byModel }: { byModel: ModelThroughput[] }) {
  if (!byModel || byModel.length === 0) {
    return <>Nothing generated in the last minute.</>;
  }
  return (
    <>
      {byModel.map((m, i) => (
        <span key={m.model}>
          {i > 0 && ' · '}
          {/* The served name is a repo path and runs long; the tail is the
              part that distinguishes one model from another. */}
          <span title={m.model}>{shortModel(m.model)}</span>{' '}
          <span className="fw-semibold">{formatTps(m.tokens_per_sec)}</span>
        </span>
      ))}
      {' tok/s'}
    </>
  );
}

// shortModel trims a served name to its last path segment, which is what
// distinguishes "cyankiwi/GLM-4.7-Flash-AWQ-4bit" from
// "openrouter/qwen/qwen3.7-flash" in the width a caption has.
function shortModel(model: string): string {
  const tail = model.split('/').pop() ?? model;
  return tail || model;
}
